"use client";

import React, { FC, memo, useMemo } from "react";
import { cn } from "utils";
import { AssetImage } from "@ospweb/sdk-ui";
import { AssetItemProps } from "./types";
import { formatBigNumber, numberStatus } from "@/src/utils";
import CheckBox from "@/src/components/CheckBox";

/**
 * AssetItem 单个资产项组件
 * 显示单个资产的基本信息，包括复选框、图标、代码和涨跌幅
 * 支持桌面端和 H5 两种布局模式
 *
 * @param props AssetItemProps
 * @returns React.ReactElement
 */
export const AssetItem: FC<AssetItemProps> = memo(
  ({ asset, isSelected, onSelectionChange, className }) => {
    /**
     * 处理复选框点击事件
     */
    const handleCheckboxClick = () => {
      onSelectionChange(asset.id, !isSelected);
    };

    /**
     * 处理容器点击事件
     * 如果点击的是复选框，则不处理（避免重复触发）
     */
    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      // 如果点击的是复选框或其子元素，不处理
      if (
        target.closest("button") ||
        target.tagName === "BUTTON" ||
        target.closest('[role="button"]')
      ) {
        return;
      }
      handleCheckboxClick();
    };

    /**
     * 格式化涨跌幅显示
     */
    const formattedChangePercent = useMemo(() => {
      if (asset.changePercent === undefined) {
        return null;
      }

      const { isNull, absValue, isPositive, isNegative } = numberStatus(
        asset.changePercent,
      );

      if (isNull) {
        return null;
      }

      const percentage = formatBigNumber(absValue, {
        needFormat: true,
        precision: 2,
        trimZeros: true,
      });

      return {
        value: `${isNegative ? "-" : isPositive ? "+" : ""}${percentage}%`,
        isPositive,
      };
    }, [asset.changePercent]);

    return (
      <>
        {/* 桌面端布局：网格布局，显示代码和名称 */}
        <div
          data-openreplay-label="WatchlistAddAssets · AssetItem · div"
          className={cn(
            "hidden md:flex gap-[11px] items-center py-4 rounded-3 cursor-pointer overflow-clip",
            className,
          )}
          onClick={handleContainerClick}
        >
          {/* 复选框 */}
          <CheckBox
            data-openreplay-label="WatchlistAddAssets · AssetItem · CheckBox"
            checked={isSelected}
            onClick={handleCheckboxClick}
            classNames="w-4 h-4 shrink-0"
            iconSize={16}
          />

          {/* 资产图标和信息容器 */}
          <div className="flex flex-1 gap-2 items-center min-w-0">
            {/* 资产图标 */}
            <div className="flex-shrink-0 border border-border_solid rounded-full overflow-hidden">
              <AssetImage
                src={asset.iconUrl}
                alt={asset.name}
                width={32}
                height={32}
                className="rounded-full"
              />
            </div>

            {/* 资产信息 - 水平排列 */}
            <div className="flex gap-[2px] items-center min-w-0">
              {/* 代码 */}
              <span className="body_medium_m text-content_primary shrink-0">
                {asset.ticker}
              </span>
              {/* 名称 */}
              <span className="body_s text-content_tertiary shrink-0">
                {asset.name}
              </span>
            </div>
          </div>
        </div>

        {/* H5 布局：垂直列表，显示代码和涨跌幅 */}
        <div
          data-openreplay-label="WatchlistAddAssets · AssetItem · div"
          className={cn(
            "md:hidden flex items-center justify-between px-0 py-3 rounded-3 cursor-pointer overflow-clip",
            className,
          )}
          onClick={handleContainerClick}
        >
          {/* 左侧：复选框、图标、代码 */}
          <div className="flex gap-2 items-center flex-1 min-w-0">
            {/* 复选框 */}
            <CheckBox
              data-openreplay-label="WatchlistAddAssets · AssetItem · CheckBox"
              checked={isSelected}
              onClick={handleCheckboxClick}
              classNames="w-4 h-4 shrink-0"
              iconSize={16}
            />

            {/* 资产图标和代码 */}
            <div className="flex gap-1 items-center flex-1 min-w-0">
              {/* 资产图标 */}
              <div className="flex-shrink-0 border border-border_solid rounded-full overflow-hidden">
                <AssetImage
                  src={asset.iconUrl}
                  alt={asset.name}
                  width={24}
                  height={24}
                  className="rounded-full"
                />
              </div>

              {/* 代码 */}
              <span className="body_medium_m text-content_primary shrink-0">
                {asset.ticker}
              </span>
            </div>
          </div>

          {/* 右侧：涨跌幅 */}
          {formattedChangePercent && (
            <p
              className={cn(
                "body_medium_s shrink-0",
                formattedChangePercent.isPositive
                  ? "text-content_accent1_primary"
                  : "text-content_accent2_primary",
              )}
            >
              {formattedChangePercent.value}
            </p>
          )}
        </div>
      </>
    );
  },
);

AssetItem.displayName = "AssetItem";
